import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
  CopilotSuggestion,
  CopilotSuggestionType,
  MissingTravelField,
} from '@bandido/shared/types';
import { useUpdateClient } from '@frontend/features/clients/queries';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

import { useUpdateCaseCustomFieldValue } from '../../queries';
import { CopilotSuggestionCard } from '../CopilotSuggestionCard';
import { TravelFacetBar } from '../TravelFacetBar';
import { CaptureEntry } from '../../traveler-capture/CaptureEntry';

interface CopilotPanelProps {
  caseId?: number;
  clientId?: string;
  /** Prellena el input del chat con el texto sugerido (SUGGEST_REPLY). */
  onUseReply?: (text: string) => void | boolean | Promise<void | boolean>;
  onApplied?: () => void;
  /** Pide sugerencias de respuesta on-demand (modelo activo). */
  onRequestReply?: () => Promise<void>;
  /** Pide un facet de viaje on-demand (botones de la barra). */
  onRequestFacet?: (
    facet: string,
    overrides?: Record<string, string>,
  ) => Promise<{ missingFields: MissingTravelField[]; found: boolean; throttled: boolean }>;
  width?: number | string;
  suggestions: CopilotSuggestion[];
  dismiss: (index: number) => void;
  isConnected: boolean;
}

const ACTIONABLE_TYPES = new Set<CopilotSuggestionType>([
  CopilotSuggestionType.UPDATE_CLIENT_NAME,
  CopilotSuggestionType.UPDATE_CUSTOM_FIELD,
  CopilotSuggestionType.SUGGEST_REPLY,
]);

export const CopilotPanel = ({
  caseId,
  clientId,
  onUseReply,
  onApplied,
  onRequestReply,
  onRequestFacet,
  width = 320,
  suggestions,
  dismiss,
  isConnected,
}: CopilotPanelProps) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();

  const updateClient = useUpdateClient();
  const updateCustomField = useUpdateCaseCustomFieldValue();
  const [applyingIndex, setApplyingIndex] = useState<number | null>(null);
  const [requesting, setRequesting] = useState(false);

  const requestSuggestion = async () => {
    if (!caseId || !onRequestReply) return;
    setRequesting(true);
    try {
      // El hook usa la respuesta HTTP como fallback y además escucha el WebSocket.
      await onRequestReply();
    } catch {
      addToast({ message: t('copilot.panel.requestError'), color: 'error' });
    } finally {
      setRequesting(false);
    }
  };

  const handleApply = async (suggestion: CopilotSuggestion, index: number) => {
    setApplyingIndex(index);
    try {
      switch (suggestion.type) {
        case CopilotSuggestionType.UPDATE_CLIENT_NAME: {
          if (!clientId) return;
          const [firstName, ...rest] = suggestion.suggestedName.trim().split(' ');
          await updateClient.mutateAsync({
            clientId,
            firstName,
            lastName: rest.join(' '),
          });
          break;
        }
        case CopilotSuggestionType.UPDATE_CUSTOM_FIELD: {
          if (!caseId) return;
          await updateCustomField.mutateAsync({
            caseId,
            fieldBlockId: suggestion.fieldBlockId,
            fieldName: suggestion.fieldName,
            value: suggestion.value,
          });
          break;
        }
        case CopilotSuggestionType.SUGGEST_REPLY:
          if (!onUseReply || (await onUseReply(suggestion.text)) === false) return;
          break;
        default:
          return;
      }
      addToast({ message: t('copilot.panel.applied'), color: 'success' });
      if (suggestion.type !== CopilotSuggestionType.SUGGEST_REPLY) onApplied?.();
      dismiss(index);
    } catch {
      addToast({ message: t('copilot.panel.applyError'), color: 'error' });
    } finally {
      setApplyingIndex(null);
    }
  };

  return (
    <Box
      width={width}
      display="flex"
      flexDirection="column"
      gap={1.5}
      // Altura acotada + minHeight:0 para que el área de cards pueda scrollear
      // (sin esto, el flex item crece y desborda en vez de scrollear).
      sx={{ height: '100%', minHeight: 0 }}
    >
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" flexShrink={0}>
        <AutoAwesomeIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
          {t('copilot.panel.title')}
        </Typography>
        {!isConnected && (
          <Chip
            size="small"
            label={t('copilot.panel.offline')}
            color="default"
            variant="outlined"
          />
        )}
        {caseId && onRequestReply && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            disabled={requesting}
            onClick={requestSuggestion}
          >
            {t('copilot.panel.requestSuggestion')}
          </Button>
        )}
      </Stack>
      <Divider />
      {caseId && <CaptureEntry key={caseId} caseId={caseId} hasClient={Boolean(clientId)} />}

      {/* Área scrolleable: header/divider quedan fijos, las cards scrollean. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
        {/* Stack con altura natural (no se encoge) dentro del contenedor
            acotado → cuando hay muchas cards, el contenedor scrollea. */}
        <Stack spacing={1.5}>
          {suggestions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('copilot.panel.noSuggestions')}
            </Typography>
          ) : (
            suggestions.map((suggestion, index) => (
              <CopilotSuggestionCard
                key={`${suggestion.type}-${index}`}
                suggestion={suggestion}
                applying={applyingIndex === index}
                onApply={
                  ACTIONABLE_TYPES.has(suggestion.type) &&
                  (suggestion.type !== CopilotSuggestionType.SUGGEST_REPLY || Boolean(onUseReply))
                    ? () => handleApply(suggestion, index)
                    : undefined
                }
                onDismiss={() => dismiss(index)}
              />
            ))
          )}
        </Stack>
      </Box>

      {/* Barra de acciones rápidas de inteligencia de viaje (on-demand). */}
      {caseId && onRequestFacet && (
        <TravelFacetBar caseId={caseId} onRequestFacet={onRequestFacet} />
      )}
    </Box>
  );
};
